import { useMemo, useState } from 'react'
import {
  DemoHeader,
  InsightCard,
  OntologyResponsePanel,
  UserActionPanel,
} from './GoalDrivenDemo'
import { useT } from '../../lib/i18n'
import type { LabDemoProps } from './types'
import type { WhatIfLocale } from '../../lib/locales'

type Status = 'standard' | 'watch' | 'sour'

const STATUS_COLOR: Record<Status, string> = {
  standard: 'var(--color-orion-pass)',
  watch: 'var(--color-orion-warn)',
  sour: 'var(--color-orion-block)',
}

function classify(h2sPpm: number, threshold: number): Status {
  if (h2sPpm > threshold) return 'sour'
  if (h2sPpm > 0.5 * threshold) return 'watch'
  return 'standard'
}

interface AssetTrajectory {
  asset: WhatIfLocale['assets'][number]
  statuses: Status[]
  firstFlipMonth: number | null
  firstFlipFrom: Status | null
  firstFlipTo: Status | null
}

export function WhatIfDemo({ paradigm, lab, data }: LabDemoProps) {
  const t = useT()
  const demo = (data as typeof t.labDemos.whatIf | undefined) ?? t.labDemos.whatIf
  const [scenarioId, setScenarioId] = useState<string>(demo.scenarios[0]?.id ?? '')
  const [horizon, setHorizon] = useState<number>(demo.horizonRange.default)

  const scenario = demo.scenarios.find((s) => s.id === scenarioId) ?? demo.scenarios[0]
  const baselineHorizon = demo.horizonRange.default

  const trajectories: AssetTrajectory[] = useMemo(() => {
    if (!scenario) return []
    const rate = scenario.h2sGrowthPctPerMonth / 100
    return demo.assets.map((a) => {
      const statuses: Status[] = []
      for (let m = 0; m <= horizon; m++) {
        const h2s = a.h2sPpmStart * Math.pow(1 + rate, m)
        statuses.push(classify(h2s, demo.baseThresholdPpm))
      }
      let firstFlipMonth: number | null = null
      let firstFlipFrom: Status | null = null
      let firstFlipTo: Status | null = null
      for (let m = 1; m <= horizon; m++) {
        if (statuses[m] !== statuses[m - 1]) {
          firstFlipMonth = m
          firstFlipFrom = statuses[m - 1]
          firstFlipTo = statuses[m]
          break
        }
      }
      return { asset: a, statuses, firstFlipMonth, firstFlipFrom, firstFlipTo }
    })
  }, [scenario, demo.assets, demo.baseThresholdPpm, horizon])

  return (
    <div className="space-y-5">
      <DemoHeader paradigm={paradigm} />

      <div className="flex flex-col gap-4">
        <UserActionPanel
          label={lab.userActionLabel}
          title={demo.scenariosLabel}
          hint={demo.scenariosHint}
        >
          <div className="grid sm:grid-cols-3 gap-2 mb-4">
            {demo.scenarios.map((s) => {
              const active = s.id === scenarioId
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setScenarioId(s.id)}
                  aria-pressed={active}
                  className={`text-left p-3 rounded-[var(--radius-md)] transition-colors ${
                    active
                      ? 'bg-[color:var(--color-orion-amber)]/15 border border-[color:var(--color-orion-amber)]/60'
                      : 'border-faint hover:bg-[color:var(--color-orion-deep)]/40'
                  }`}
                >
                  <div
                    className={`mono text-[length:var(--text-small)] font-medium mb-1 ${
                      active ? 'text-[color:var(--color-orion-amber)]' : 'text-[color:var(--color-orion-text)]'
                    }`}
                  >
                    {s.title}
                  </div>
                  <p
                    className="text-[color:var(--color-orion-haze)] leading-snug"
                    style={{ fontSize: 'var(--text-micro)' }}
                  >
                    {s.description}
                  </p>
                </button>
              )
            })}
          </div>

          <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
            <div className="flex items-baseline gap-3">
              <span className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)]">
                {demo.horizonLabel}
              </span>
              <span className="mono text-[length:var(--text-body)] text-[color:var(--color-orion-text)]">
                {horizon} {demo.monthsUnit}
              </span>
            </div>
            {horizon !== baselineHorizon && (
              <button
                type="button"
                onClick={() => setHorizon(baselineHorizon)}
                className="mono text-[length:var(--text-micro)] uppercase tracking-widest px-3 py-1.5 rounded-[var(--radius-sm)] border-faint text-[color:var(--color-orion-haze)] hover:text-[color:var(--color-orion-text)] hover:bg-[color:var(--color-orion-night)] transition-colors"
              >
                {demo.resetButton}
              </button>
            )}
          </div>
          <input
            type="range"
            min={demo.horizonRange.min}
            max={demo.horizonRange.max}
            step={demo.horizonRange.step}
            value={horizon}
            onChange={(e) => setHorizon(Number(e.target.value))}
            aria-label={demo.horizonLabel}
            className="w-full accent-[color:var(--color-orion-amber)]"
          />
          <div className="flex justify-between mono text-[length:var(--text-micro)] text-[color:var(--color-orion-haze)]">
            <span>{demo.horizonRange.min} {demo.monthsUnit}</span>
            <span>{demo.horizonRange.max} {demo.monthsUnit}</span>
          </div>
        </UserActionPanel>

        <OntologyResponsePanel label={lab.ontologyResponseLabel}>
          <ProjectionGrid
            trajectories={trajectories}
            horizon={horizon}
            statusLabels={demo.statusLabels}
            firstFlipTpl={demo.firstFlipTpl}
            stableLabel={demo.stableLabel}
            monthAxisLabel={demo.monthAxisLabel}
            legendLabel={demo.legendLabel}
          />
        </OntologyResponsePanel>
      </div>

      <InsightCard title={demo.insightTitle} body={demo.insightBody} />
    </div>
  )
}

interface ProjectionGridProps {
  trajectories: AssetTrajectory[]
  horizon: number
  statusLabels: WhatIfLocale['statusLabels']
  firstFlipTpl: string
  stableLabel: string
  monthAxisLabel: string
  legendLabel: string
}

function ProjectionGrid({
  trajectories,
  horizon,
  statusLabels,
  firstFlipTpl,
  stableLabel,
  monthAxisLabel,
  legendLabel,
}: ProjectionGridProps) {
  const cellWidth = 22
  const months = Array.from({ length: horizon + 1 }, (_, i) => i)

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto">
        <div className="min-w-fit">
          <div
            className="grid items-center mb-2"
            style={{ gridTemplateColumns: `180px 1fr 220px` }}
          >
            <div />
            <div
              className="grid gap-0.5"
              style={{ gridTemplateColumns: `repeat(${months.length}, ${cellWidth}px)` }}
            >
              {months.map((m) => (
                <div
                  key={m}
                  className="mono text-[10px] text-[color:var(--color-orion-haze)] text-center"
                >
                  {m === 0 ? `${monthAxisLabel} 0` : m % 3 === 0 ? `M${m}` : ''}
                </div>
              ))}
            </div>
            <div />
          </div>

          {trajectories.map((row) => (
            <div
              key={row.asset.id}
              className="grid items-center py-1.5 border-t border-[color:var(--color-orion-surface)]/30"
              style={{ gridTemplateColumns: `180px 1fr 220px` }}
            >
              <div className="pr-3">
                <div className="mono text-[length:var(--text-small)] text-[color:var(--color-orion-text)] truncate">
                  {row.asset.code}
                </div>
                <div className="text-[length:var(--text-micro)] text-[color:var(--color-orion-haze)] truncate">
                  {row.asset.name}
                </div>
              </div>
              <div
                className="grid gap-0.5"
                style={{ gridTemplateColumns: `repeat(${months.length}, ${cellWidth}px)` }}
              >
                {row.statuses.map((status, m) => (
                  <div
                    key={m}
                    title={`${monthAxisLabel} ${m} · ${statusLabels[status]}`}
                    className="h-5 rounded-[2px]"
                    style={{
                      background: `color-mix(in oklch, ${STATUS_COLOR[status]} 65%, transparent)`,
                    }}
                  />
                ))}
              </div>
              <div className="pl-3 text-right">
                {row.firstFlipMonth !== null && row.firstFlipFrom && row.firstFlipTo ? (
                  <span className="mono text-[length:var(--text-micro)] text-[color:var(--color-orion-amber)]">
                    {firstFlipTpl
                      .replace('{month}', String(row.firstFlipMonth))
                      .replace('{from}', statusLabels[row.firstFlipFrom])
                      .replace('{to}', statusLabels[row.firstFlipTo])}
                  </span>
                ) : (
                  <span className="mono text-[length:var(--text-micro)] text-[color:var(--color-orion-haze)]">
                    {stableLabel}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap pt-2 border-t border-[color:var(--color-orion-surface)]/30">
        <span className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-haze)]">
          {legendLabel}
        </span>
        {(['standard', 'watch', 'sour'] as Status[]).map((s) => (
          <div key={s} className="flex items-center gap-1.5">
            <span
              className="inline-block w-3 h-3 rounded-[2px]"
              style={{ background: `color-mix(in oklch, ${STATUS_COLOR[s]} 65%, transparent)` }}
            />
            <span className="text-[length:var(--text-small)] text-[color:var(--color-orion-text)]">
              {statusLabels[s]}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
